import axios from 'axios';

// Defensive: fall back to same-origin if REACT_APP_BACKEND_URL is missing or invalid.
// This makes the build resilient when deployed under a custom domain where the
// baked-in env var may not match the current host.
function resolveBackendUrl() {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (raw && raw !== 'undefined' && /^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }
  return '';
}

const BACKEND_URL = resolveBackendUrl();
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'biosites_token_v1';

export { BACKEND_URL, API };

export function getToken() { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } }
export function setToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}
export function isAuthenticated() { return !!getToken(); }
export function logout() { setToken(null); }

const client = axios.create({ baseURL: API, timeout: 20000 });
client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401) {
      const url = err.config?.url || '';
      if (!url.includes('/auth/login')) setToken(null);
    }
    return Promise.reject(err);
  }
);

// Auth
export async function login(username, password) {
  const { data } = await client.post('/auth/login', { username, password });
  setToken(data.token);
  return data;
}
export async function verifyToken() { try { await client.get('/auth/verify'); return true; } catch { return false; } }
export async function getMe() { const { data } = await client.get('/auth/me'); return data; }
export async function changePassword(currentPassword, newPassword) {
  const { data } = await client.post('/auth/change-password', { currentPassword, newPassword });
  return data;
}
export async function changeUsername(currentPassword, newUsername) {
  const { data } = await client.post('/auth/change-username', { currentPassword, newUsername });
  return data;
}

// Biosites
export async function listBiosites() { const { data } = await client.get('/biosites'); return data; }
export async function getBiosite(slug) {
  try { const { data } = await client.get(`/biosites/${slug}`); return data; }
  catch (e) { if (e.response && e.response.status === 404) return null; throw e; }
}
export async function createBiosite(biosite) { const { data } = await client.post('/biosites', biosite); return data; }
export async function updateBiosite(slug, biosite) { const { data } = await client.put(`/biosites/${slug}`, biosite); return data; }
export async function deleteBiosite(slug) { const { data } = await client.delete(`/biosites/${slug}`); return data; }

// Events / Stats
export async function trackEvent(slug, type, target = '') {
  try { await axios.post(`${API}/events`, { slug, type, target }); } catch { /* ignore */ }
}
export async function getStats(slug, range = '7d') {
  const { data } = await client.get(`/biosites/${slug}/stats`, { params: { range } });
  return data;
}

// Scheduling
export async function getAvailability(slug, date, serviceId) {
  const { data } = await axios.get(`${API}/biosites/${slug}/availability`, { params: { date, serviceId } });
  return data;
}
export async function createBooking(slug, payload) {
  const { data } = await axios.post(`${API}/biosites/${slug}/bookings`, payload);
  return data;
}
export async function listBookings(slug) { const { data } = await client.get(`/biosites/${slug}/bookings`); return data; }
export async function updateBooking(slug, bid, patch) { const { data } = await client.put(`/biosites/${slug}/bookings/${bid}`, patch); return data; }
export async function deleteBooking(slug, bid) { const { data } = await client.delete(`/biosites/${slug}/bookings/${bid}`); return data; }

export const THEMES = {
  sunset: { name: 'Pôr do Sol', bg: 'linear-gradient(180deg, #ff9a3c 0%, #ff6a3d 50%, #c9184a 100%)', button: '#ffffff', buttonText: '#1a1a1a', text: '#ffffff' },
  rose: { name: 'Rosa Suave', bg: 'linear-gradient(180deg, #fff0f3 0%, #ffb3c6 100%)', button: '#1a1a1a', buttonText: '#ffffff', text: '#1a1a1a' },
  forest: { name: 'Floresta', bg: 'linear-gradient(180deg, #2d6a4f 0%, #081c15 100%)', button: '#d8f3dc', buttonText: '#1b4332', text: '#ffffff' },
  ocean: { name: 'Oceano', bg: 'linear-gradient(180deg, #90e0ef 0%, #0077b6 100%)', button: '#ffffff', buttonText: '#03045e', text: '#ffffff' },
  mono: { name: 'Monocromático', bg: '#0f0f10', button: '#ffffff', buttonText: '#0f0f10', text: '#ffffff' },
  cream: { name: 'Creme', bg: '#f5efe6', button: '#1a1a1a', buttonText: '#ffffff', text: '#1a1a1a' },
  midnight: { name: 'Meia-noite', bg: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)', button: '#38bdf8', buttonText: '#0f172a', text: '#e2e8f0' },
  lavender: { name: 'Lavanda', bg: 'linear-gradient(180deg, #ddd6fe 0%, #a78bfa 100%)', button: '#4c1d95', buttonText: '#ffffff', text: '#1e1b4b' },
  mint: { name: 'Menta', bg: 'linear-gradient(180deg, #d1fae5 0%, #34d399 100%)', button: '#064e3b', buttonText: '#ffffff', text: '#064e3b' },
  sand: { name: 'Areia', bg: 'linear-gradient(180deg, #fef3c7 0%, #fbbf24 100%)', button: '#78350f', buttonText: '#ffffff', text: '#422006' },
  neon: { name: 'Neon', bg: '#0a0a0a', button: '#a3e635', buttonText: '#0a0a0a', text: '#f5f5f5' },
  bubblegum: { name: 'Chiclete', bg: 'linear-gradient(180deg, #fda4af 0%, #f472b6 100%)', button: '#ffffff', buttonText: '#be185d', text: '#500724' },
};
export function resolveTheme(biosite) {
  if (biosite?.customTheme && (biosite.customTheme.bg || biosite.customTheme.bgGradient)) {
    const c = biosite.customTheme;
    return { name: 'Personalizado', bg: c.bgGradient || c.bg || '#0f0f10', button: c.button || '#ffffff', buttonText: c.buttonText || '#000000', text: c.text || '#ffffff' };
  }
  return THEMES[biosite?.theme] || THEMES.mono;
}
export function shapeRadius(shape) {
  if (shape === 'square') return '6px';
  if (shape === 'pill') return '9999px';
  return '16px';
}
export function evalBusinessHours(bh, now = new Date()) {
  if (!bh || !bh.enabled) return null;
  const day = String(now.getDay());
  const d = bh.days?.[day];
  const mins = now.getHours() * 60 + now.getMinutes();
  const toMin = (s) => { const [h, m] = (s || '0:0').split(':').map((x) => parseInt(x, 10) || 0); return h * 60 + m; };
  if (d && d.enabled) {
    const o = toMin(d.open), c = toMin(d.close);
    if (mins >= o && mins < c) return { open: true, label: 'Aberto agora', detail: `Fecha às ${d.close}` };
    if (mins < o) return { open: false, label: 'Fechado', detail: `Abre hoje às ${d.open}` };
  }
  for (let i = 1; i <= 7; i++) {
    const idx = String((now.getDay() + i) % 7);
    const dd = bh.days?.[idx];
    if (dd && dd.enabled) {
      const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
      return { open: false, label: 'Fechado', detail: `Abre ${dayNames[parseInt(idx)]} às ${dd.open}` };
    }
  }
  return { open: false, label: 'Fechado' };
}
